import React, { Component } from "react";
import {
  Table,
  Header,
  Dropdown,
  Button,
  Icon,
  Input,
  Message,
} from "semantic-ui-react";

import { resolveGradingScheme } from "../../utils/gradingScheme";

/**
 * One club's members, and their marks for the term.
 *
 * A club mark is a number, for every member, whatever grade they are in.
 *
 * It used to follow each learner's own grading scheme - a number for Grade 5, a
 * letter for Grade 1 while Grade 1 was graded in letters - which split one
 * club's marks across two columns. The day Grade 1 moved to numbers, the marks
 * already entered for its members became invisible: the roster read the numeric
 * column, the marks were in the letter one, and a moderator entered them again
 * and watched them vanish again. A club spans grade levels; how a learner's
 * academic subjects are graded has nothing to do with it.
 */
class ClubRoster extends Component {
  state = { adding: "", edits: {} };

  /** The numeric column for the term on screen. The same one for everybody. */
  fieldFor() {
    const scheme = resolveGradingScheme(this.props.schoolYear, null);
    const index = parseInt(this.props.currentQuarter, 10) || 1;
    const period = scheme.periods.filter((p) => p.index === index)[0];

    return period ? period.dbField : "firstQuarter";
  }

  valueOf(member) {
    const key = member.clubStudentId;
    if (this.state.edits[key] !== undefined) return this.state.edits[key];

    const stored = member[this.fieldFor()];
    if (stored === undefined || stored === null) return "";
    // A zero is "no mark yet" here, the same as it is on a grade sheet.
    return stored === 0 ? "" : String(stored);
  }

  setEdit = (clubStudentId, value) =>
    this.setState((prev) => ({
      edits: { ...prev.edits, [clubStudentId]: value },
    }));

  save = (member) => {
    const raw = this.state.edits[member.clubStudentId];
    if (raw === undefined) return;

    this.props.onSaveMarks(member, this.fieldFor(), Number(raw) || 0);
    this.setState((prev) => {
      const edits = { ...prev.edits };
      delete edits[member.clubStudentId];
      return { edits };
    });
  };

  renderMarkCell(member) {
    const value = this.valueOf(member);
    const dirty = this.state.edits[member.clubStudentId] !== undefined;

    // A club is its moderator's subject. Everyone else reads the mark, the
    // same way nobody but a subject teacher types a subject's grades.
    if (!this.props.mayEnterMarks) return <span>{value || ""}</span>;

    return (
      <Input
        size="small"
        style={{ width: "6em" }}
        value={value}
        placeholder="-"
        onChange={(e, d) => this.setEdit(member.clubStudentId, d.value)}
        onBlur={() => dirty && this.save(member)}
      />
    );
  }

  render() {
    const { club, members, learners, busy, currentQuarter } = this.props;

    const rows = members
      .map((member) => ({
        member,
        learner: learners[member.studentId] || {
          name: "Unknown learner",
          gradeLevel: "",
          sectionName: "",
        },
      }))
      .sort((a, b) => a.learner.name.localeCompare(b.learner.name));

    const memberIds = members.map((m) => m.studentId);
    const options = Object.keys(learners)
      .filter((studentId) => memberIds.indexOf(studentId) === -1)
      .map((studentId) => {
        const l = learners[studentId];
        return {
          key: studentId,
          value: studentId,
          text: l.gradeLevel ? `${l.name} - ${l.gradeLevel}` : l.name,
        };
      })
      .sort((a, b) => a.text.localeCompare(b.text));

    const scheme = resolveGradingScheme(this.props.schoolYear, null);
    const period =
      scheme.periods.filter((p) => p.index === parseInt(currentQuarter, 10))[0] ||
      scheme.periods[0];

    return (
      <div>
        <Header as="h4">
          {club.name} - members
          <Header.Subheader>
            {this.props.mayEnterMarks
              ? `Marks below are for ${period ? period.label : "the current term"}. ` +
                "Change the term at the top of the page to enter another."
              : `Showing ${period ? period.label : "this term"}. The marks are ` +
                "read-only for you at this point on the approval ladder."}
          </Header.Subheader>
        </Header>

        {this.props.approval}

        {this.props.mayEditRoster && (
        <Dropdown
          search
          selection
          placeholder="Add a learner to this club"
          style={{ minWidth: "26em", marginBottom: "1em" }}
          options={options}
          value={this.state.adding}
          disabled={busy || options.length === 0}
          onChange={(e, d) => {
            this.props.onAddMember(d.value);
            this.setState({ adding: "" });
          }}
        />
        )}

        {members.length === 0 ? (
          <Message>
            {this.props.mayEditRoster
              ? `Nobody is in ${club.name} yet. Add learners above - they can come from any grade level.`
              : `Nobody is in ${club.name} yet. The registrar puts learners in.`}
          </Message>
        ) : (
          <Table celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Learner</Table.HeaderCell>
                <Table.HeaderCell collapsing>Grade Level</Table.HeaderCell>
                <Table.HeaderCell collapsing>Section</Table.HeaderCell>
                <Table.HeaderCell collapsing textAlign="center">
                  {period ? period.shortLabel : "Mark"}
                </Table.HeaderCell>
                <Table.HeaderCell collapsing textAlign="center">
                  Actions
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map(({ member, learner }) => (
                <Table.Row key={member.clubStudentId}>
                  <Table.Cell>{learner.name}</Table.Cell>
                  <Table.Cell collapsing>{learner.gradeLevel}</Table.Cell>
                  <Table.Cell collapsing>{learner.sectionName}</Table.Cell>
                  <Table.Cell collapsing textAlign="center">
                    {this.renderMarkCell(member)}
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="center">
                    {this.props.mayEditRoster && (
                      <Button
                        icon
                        size="tiny"
                        title="Take this learner out of the club"
                        disabled={busy}
                        onClick={() => this.props.onRemoveMember(member)}
                      >
                        <Icon name="trash" />
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    );
  }
}

export default ClubRoster;
